import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'chapters' | 'info'>('chapters');
  const [showNotFound, setShowNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const comic = useMemo(() => comics.find((c) => c.id === id), [id]);
  const category = useMemo(
    () => (comic ? categories.find((c) => c.slug === comic.categorySlug) : undefined),
    [comic]
  );

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.id !== comic.id && c.categorySlug === comic.categorySlug)
      .slice(0, 4);
  }, [comic]);

  useEffect(() => {
    if (!comic && id) {
      const timer = setTimeout(() => setShowNotFound(true), 300);
      return () => clearTimeout(timer);
    }
    setShowNotFound(false);
  }, [comic, id]);

  if (!comic) {
    if (!showNotFound) {
      return (
        <>
          <Navbar />
          <main className="pt-20 pb-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-center min-h-[60vh]">
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-sm text-foreground-500">加载中...</p>
              </div>
            </div>
          </main>
          <Footer />
        </>
      );
    }
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6 flex flex-col items-center justify-center min-h-[60vh]">
            <i className="ri-file-unknow-line text-6xl text-foreground-300 w-16 h-16 flex items-center justify-center mb-4"></i>
            <h1 className="text-xl font-semibold text-foreground-800 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已下架</p>
            <Link
              to="/"
              className="px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫大全`,
    description: comic.description.slice(0, 150),
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[0]?.updateTime || '2026-01-01',
    dateModified: comic.updateTime,
    image: comic.cover,
    publisher: {
      '@type': 'Organization',
      name: '韩漫大全',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://hanmandq.com/article/${comic.id}`,
    },
  };

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  return (
    <>
      <SEOHead
        title={`${comic.title}_${comic.author}_韩漫大全在线阅读`}
        description={comic.description.slice(0, 150)}
        keywords={`韩漫大全,${comic.title},${comic.author},韩国漫画,免费在线阅读,${comic.tags.join(',')}`}
        canonicalUrl={`https://hanmandq.com/article/${comic.id}`}
        ogImage={comic.cover}
        ogType="article"
        schemaData={articleSchema}
      />
      <Navbar />
      <main className="pt-20 pb-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: category?.name || comic.category, path: `/category/${comic.categorySlug}` },
              { name: comic.title },
            ]}
          />

          <article>
            <div className="flex flex-col md:flex-row gap-8 mb-12">
              <div className="md:w-72 shrink-0">
                <div className="rounded-xl overflow-hidden bg-background-200">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 韩漫大全 韩国漫画`}
                    className="w-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-600 mb-4">
                  <strong>作者：</strong>{comic.author}
                </p>

                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <span
                    className={`text-xs px-3 py-1 rounded-full font-medium ${
                      comic.status === '连载中'
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-secondary-100 text-secondary-700'
                    }`}
                  >
                    {comic.status}
                  </span>
                  <span className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-700 font-medium">
                    {comic.category}
                  </span>
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-3 py-1 rounded-full bg-background-200 text-foreground-600"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-6 mb-6 text-sm text-foreground-600">
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500 w-4 h-4 flex items-center justify-center"></i>
                    <strong>{comic.rating}</strong> 分
                  </span>
                  <span>
                    <strong>{formatViews(comic.views)}</strong> 阅读
                  </span>
                  <span>最近更新：<strong>{comic.updateTime}</strong></span>
                </div>

                <div className="flex items-center gap-2">
                  <span
                    onClick={() => setActiveTab('chapters')}
                    className={`px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${
                      activeTab === 'chapters'
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    章节目录
                  </span>
                  <span
                    onClick={() => setActiveTab('info')}
                    className={`px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${
                      activeTab === 'info'
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    作品简介
                  </span>
                </div>
              </div>
            </div>

            {activeTab === 'chapters' && (
              <section className="mb-12">
                <h2 className="font-heading text-xl text-foreground-900 mb-5">章节目录</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {comic.chapters.map((ch) => (
                    <div
                      key={ch.id}
                      className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 border border-background-200/60 hover:border-primary-300 transition-colors cursor-pointer group"
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 text-xs font-semibold shrink-0">
                          {ch.number}
                        </span>
                        <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors line-clamp-1">
                          {ch.title}
                        </span>
                      </div>
                      <span className="text-xs text-foreground-500 whitespace-nowrap ml-2">{ch.updateTime}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {activeTab === 'info' && (
              <section className="mb-12">
                <h2 className="font-heading text-xl text-foreground-900 mb-5">作品简介</h2>
                <div className="bg-background-100 rounded-xl p-6 border border-background-200/60">
                  <p className="text-sm text-foreground-700 leading-relaxed">{comic.description}</p>
                </div>
              </section>
            )}

            {relatedComics.length > 0 && (
              <section>
                <h2 className="font-heading text-xl text-foreground-900 mb-5">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                  {relatedComics.map((rc) => (
                    <ComicCard
                      key={rc.id}
                      id={rc.id}
                      title={rc.title}
                      author={rc.author}
                      cover={rc.cover}
                      status={rc.status}
                      rating={rc.rating}
                      views={rc.views}
                      updateTime={rc.updateTime}
                    />
                  ))}
                </div>
              </section>
            )}
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}